import React, { useState, useEffect } from 'react';
import { View, StyleSheet, ScrollView, Alert, ActivityIndicator } from 'react-native';
import { Button, TextInput, Surface, Text, DataTable, Portal, Modal } from 'react-native-paper';
import { DatePickerInput } from 'react-native-paper-dates';
import { pt, registerTranslation } from 'react-native-paper-dates';
import { Picker } from '@react-native-picker/picker';
import { useNavigation, useRoute } from '@react-navigation/native';
import { supabase } from '../services/supabase';
import { format, parseISO } from 'date-fns';
import api, { apiService } from '../services/api';

// Registra a tradução em português
registerTranslation('pt', pt);

interface StatusCobranca {
  S_EXIGEDATA: string;
  S_STATUSCOB: string;
  I_PRIORIDADE: string;
  I_DIASRETORNO: string;
  I_CODSTATUSCOB: string;
}

export function AgendamentoMultiplo() {
  const navigation = useNavigation();
  const route = useRoute<any>();
  const { titulos } = route.params;

  const [data, setData] = useState<Date | undefined>(undefined);
  const [status, setStatus] = useState('');
  const [observacao, setObservacao] = useState('');
  const [statusList, setStatusList] = useState<StatusCobranca[]>([]);
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState<{ empresa: string; codusur: string } | null>(null);
  const [selectedTitulo, setSelectedTitulo] = useState<any>(null);
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [showAllTitulos, setShowAllTitulos] = useState(false);
  const INITIAL_SHOW_COUNT = 3;

  const isFormValid = () => {
    return data !== undefined && status !== '';
  };

  const fetchUserAndStatusData = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      const { data: userDataResponse, error: userError } = await supabase
        .from('usuarios')
        .select('empresa, codusur')
        .eq('id', user.id)
        .single();

      if (userError) throw userError;
      if (!userDataResponse?.empresa) throw new Error('Empresa não encontrada');

      const { data, error } = await supabase
        .from('listasconsultaapi')
        .select('data')
        .eq('rotaapi', 'FINANCEIROstatuscob')
        .eq('codempresa', userDataResponse.empresa)
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (data && data[0]?.data) {
        setStatusList(data[0].data);
      }

      setUserData(userDataResponse);
    } catch (error) {
      console.error('Erro ao buscar dados:', error);
      Alert.alert('Erro', 'Não foi possível carregar os dados necessários');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUserAndStatusData();
  }, []);

  const handleSave = async () => {
    if (!isFormValid()) {
      Alert.alert('Erro', 'Por favor, preencha todos os campos obrigatórios corretamente');
      return;
    }

    try {
      const selectedStatus = statusList.find(s => s.I_CODSTATUSCOB === status);
      if (!selectedStatus) throw new Error('Status não selecionado');

      if (!userData) throw new Error('Dados do usuário não encontrados');

      const { data: clientData, error: clientError } = await supabase
        .from('listasconsultaapi')
        .select('data')
        .eq('rotaapi', 'FINANCEIROListaClienteDeb')
        .eq('codempresa', userData.empresa)
        .order('created_at', { ascending: false })
        .limit(1);

      if (clientError) throw clientError;
      if (!clientData?.[0]?.data) throw new Error('Dados do cliente não encontrados');

      // Processar cada título
      for (const titulo of titulos) {
        const cliente = clientData[0].data.find((c: { I_CODCLI: string; S_TELCOB: string }) => c.I_CODCLI === titulo.I_CODCLI);
        if (!cliente) {
          console.warn(`Cliente não encontrado para título ${titulo.S_DUPLICPREST}`);
          continue;
        }

        const requestData1 = {
          codfunc: userData.codusur,
          codcli: titulo.I_CODCLI,
          telcob: cliente.S_TELCOB,
          obs1: observacao,
          statuscob: selectedStatus.S_STATUSCOB,
          INSERESTATUSCOB: 'N',
          codstatuscob: status,
          DATARETORNO: data
        };

        console.log(`Dados para primeiro endpoint (Título ${titulo.S_DUPLICPREST}):`, requestData1);
        if (!userData) throw new Error('Dados do usuário não encontrados');
        await apiService.insertHistoricoCob(JSON.stringify(requestData1), userData.empresa);

        const requestData2 = {
          codfilial: titulo.I_CODFILIAL,
          numtransvenda: titulo.I_NUMTRANSVENDA,
          duplic: titulo.I_DUPLIC,
          prest: titulo.S_PREST,
          statuscob: selectedStatus.S_STATUSCOB,
          INSERESTATUSCOB: 'N',
          codstatuscob: status,
          codcli: titulo.I_CODCLI
        };

        console.log(`Dados para segundo endpoint (Título ${titulo.S_DUPLICPREST}):`, requestData2);
        await apiService.insertHistoricoCobi(JSON.stringify(requestData2), userData.empresa);
      }

      Alert.alert('Sucesso', 'Agendamento de todos os títulos realizado com sucesso');
      navigation.goBack();
    } catch (error) {
      console.error('Erro no agendamento:', error);
      Alert.alert('Erro', 'Não foi possível realizar o agendamento de todos os títulos');
    }
  };

  const showDetails = (titulo: any) => {
    setSelectedTitulo(titulo);
    setDetailsVisible(true);
  };

  const hideDetails = () => {
    setSelectedTitulo(null);
    setDetailsVisible(false);
  };

  if (loading) {
    return (
      <View style={styles.loading}>
        <ActivityIndicator size="large" />
      </View>
    );
  }

  // Calcula o valor total corretamente convertendo para número
  const valorTotal = titulos.reduce((acc: number, titulo: any) => acc + parseFloat(titulo.F_VALOR), 0);

  const titulosToShow = showAllTitulos ? titulos : titulos.slice(0, INITIAL_SHOW_COUNT);
  const hiddenCount = titulos.length - INITIAL_SHOW_COUNT;

  return (
    <View style={styles.container}>
      <Surface style={styles.content}>
        <ScrollView>
          <View style={styles.summary}>
            <Text style={styles.summaryText}>
              Títulos Selecionados: {titulos.length}
            </Text>
            <Text style={styles.summaryText}>
              Valor Total: {new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL',
                minimumFractionDigits: 2,
                maximumFractionDigits: 2
              }).format(valorTotal)}
            </Text>
          </View>

          <View>
            <View style={styles.headerActions}>
              {hiddenCount > 0 && (
                <Button
                  mode="text"
                  onPress={() => setShowAllTitulos(!showAllTitulos)}
                >
                  {showAllTitulos ? 'Mostrar Menos' : `Ver +${hiddenCount} títulos`}
                </Button>
              )}
            </View>

            <DataTable style={styles.table}>
              <DataTable.Header>
                <DataTable.Title style={styles.columnDupl}>Dupl/Prest</DataTable.Title>
                <DataTable.Title numeric style={styles.columnValue}>Valor</DataTable.Title>
                <DataTable.Title style={styles.columnDate}>Vencimento</DataTable.Title>
                <DataTable.Title style={styles.columnActions}>Ações</DataTable.Title>
              </DataTable.Header>

              <ScrollView style={styles.tableScroll}>
                {titulosToShow.map((titulo: any, index: number) => (
                  <DataTable.Row key={index}>
                    <DataTable.Cell style={styles.columnDupl}>{titulo.S_DUPLICPREST}</DataTable.Cell>
                    <DataTable.Cell numeric style={styles.columnValue}>
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      }).format(parseFloat(titulo.F_VALOR))}
                    </DataTable.Cell>
                    <DataTable.Cell style={styles.columnDate}>
                      {format(parseISO(titulo.D_DTVENC), 'dd/MM/yyyy')}
                    </DataTable.Cell>
                    <DataTable.Cell style={styles.columnActions}>
                      <Button 
                        mode="text" 
                        compact 
                        onPress={() => showDetails(titulo)}
                      >
                        Ver Detalhes
                      </Button>
                    </DataTable.Cell>
                  </DataTable.Row>
                ))}
              </ScrollView>
            </DataTable>
          </View>

          <View style={styles.field}>
            <Text>Data do Próximo Contato</Text>
            <DatePickerInput
              locale="pt"
              label="Data"
              value={data}
              onChange={(date) => setData(date)}
              inputMode="start"
              style={styles.input}
              mode="outlined"
            />
          </View>

          <View style={styles.field}>
            <Text>Status da Cobrança</Text>
            <View style={styles.pickerContainer}>
              <Picker
                selectedValue={status}
                onValueChange={(itemValue) => setStatus(itemValue)}
                style={styles.picker}
              >
                <Picker.Item label="Selecione um status..." value="" />
                {statusList && statusList.map(item => (
                  <Picker.Item
                    key={item.I_CODSTATUSCOB}
                    label={item.S_STATUSCOB}
                    value={item.I_CODSTATUSCOB}
                  />
                ))}
              </Picker>
            </View>
          </View>

          <View style={styles.field}>
            <Text>Observação</Text>
            <TextInput
              mode="outlined"
              value={observacao}
              onChangeText={setObservacao}
              multiline
              numberOfLines={4}
              style={styles.textArea}
            />
          </View>

          <View style={styles.buttons}>
            <Button
              mode="outlined"
              onPress={() => navigation.goBack()}
              style={styles.button}
            >
              Cancelar
            </Button>
            <Button
              mode="contained"
              onPress={handleSave}
              style={styles.button}
              disabled={!isFormValid()}
            >
              Gravar
            </Button>
          </View>
        </ScrollView>

        <Portal>
          <Modal
            visible={detailsVisible}
            onDismiss={hideDetails}
            contentContainerStyle={styles.modalContent}
          >
            {selectedTitulo && (
              <View>
                <Text style={styles.modalTitle}>Detalhes do Título</Text>
                
                <View style={styles.modalField}>
                  <Text style={styles.modalLabel}>Duplicata/Prestação:</Text>
                  <Text style={styles.modalValue}>{selectedTitulo.S_DUPLICPREST}</Text>
                </View>

                <View style={styles.modalField}>
                  <Text style={styles.modalLabel}>Valor:</Text>
                  <Text style={styles.modalValue}>
                    {new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(selectedTitulo.F_VALOR)}
                  </Text>
                </View>

                <View style={styles.modalField}>
                  <Text style={styles.modalLabel}>Vencimento:</Text>
                  <Text style={styles.modalValue}>
                    {format(parseISO(selectedTitulo.D_DTVENC), 'dd/MM/yyyy')}
                  </Text>
                </View>

                <View style={styles.modalField}>
                  <Text style={styles.modalLabel}>Banco:</Text>
                  <Text style={styles.modalValue}>{selectedTitulo.S_CODBANCOCM}</Text>
                </View>

                <View style={styles.modalField}>
                  <Text style={styles.modalLabel}>Nosso Número:</Text>
                  <Text style={styles.modalValue}>{selectedTitulo.S_NOSSONUMBCO}</Text>
                </View>

                <View style={styles.modalField}>
                  <Text style={styles.modalLabel}>Linha Digitável:</Text>
                  <Text style={styles.modalValue}>{selectedTitulo.S_LINHADIGFORMATADA}</Text>
                </View>

                <Button mode="contained" onPress={hideDetails} style={styles.modalButton}>
                  Fechar
                </Button>
              </View>
            )}
          </Modal>
        </Portal>
      </Surface>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
    backgroundColor: '#f5f5f5',
  },
  content: {
    padding: 16,
    borderRadius: 8,
    flex: 1,
  },
  summary: {
    backgroundColor: '#f0f0f0',
    padding: 12,
    borderRadius: 8,
    marginBottom: 16,
  },
  summaryText: {
    fontSize: 16,
    fontWeight: '500',
    marginBottom: 8,
  },
  headerActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    marginBottom: 16,
  },
  table: {
    marginTop: 12,
    backgroundColor: '#fff',
  },
  tableScroll: {
    maxHeight: 200,
  },
  field: {
    marginBottom: 16,
  },
  input: {
    marginTop: 8,
  },
  pickerContainer: {
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#ccc',
    borderRadius: 4,
    backgroundColor: '#fff',
  },
  picker: {
    height: 50,
  },
  textArea: {
    marginTop: 8,
  },
  buttons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 24,
    marginBottom: 24,
  },
  button: {
    flex: 1,
    marginHorizontal: 8,
  },
  loading: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContent: {
    backgroundColor: 'white',
    padding: 20,
    margin: 20,
    borderRadius: 8,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    marginBottom: 16,
  },
  modalField: {
    marginBottom: 12,
  },
  modalLabel: {
    fontSize: 14,
    color: '#666',
  },
  modalValue: {
    fontSize: 16,
    marginTop: 4,
  },
  modalButton: {
    marginTop: 16,
  },
  columnDupl: {
    flex: 2,
    paddingRight: 8,
  },
  columnValue: {
    flex: 2,
    paddingHorizontal: 8,
    justifyContent: 'flex-end',
  },
  columnDate: {
    flex: 2,
    paddingHorizontal: 8,
    justifyContent: 'center',
  },
  columnActions: {
    flex: 2,
    paddingLeft: 8,
  },
  valueCell: {
    textAlign: 'right',
  },
  dateCell: {
    textAlign: 'center',
  },
});
